import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '@/hooks/useSession';
import { Skeleton } from '@/components/ui/Skeleton';

type RequireAuthProps = {
  children: ReactNode;
};

/**
 * Gate a route on authenticated session presence. While the session
 * query is loading, render a calm skeleton so we don't flash a Sign-in
 * redirect on a logged-in user who just hard-refreshed.
 */
export function RequireAuth({ children }: RequireAuthProps) {
  const session = useSession();
  const location = useLocation();

  if (session.isLoading) {
    return (
      <div className="container-prose py-20">
        <Skeleton className="h-6 w-40 mx-auto mb-4" />
        <Skeleton className="h-4 w-64 mx-auto" />
      </div>
    );
  }

  if (!session.data) {
    const nextPath = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(nextPath)}`}
        replace
      />
    );
  }

  return <>{children}</>;
}
